/**
 * Given an object, this will recursively remove keys where the value
 * is "empty" (undefined, null, and blank string). This mutates the
 * original object. If the result is an empty object, this will return
 * undefined, since the object itself is empty.
 *
 * @param {Object} input - The object to recursively clean.
 * @param {Object} [options] - If truthy, "empty" values will set to `null` instead of removed.
 * @param {Boolean} [options.setToNull] - If truthy, "empty" values will set to `null` instead of removed.
 * @param {Boolean} [options.preserveEmptyValues] - If truthy, "empty" values are preserved, but empty objects and arrays are removed.
 * @return {Object|undefined} The mutated object, or undefined.
 */
export default (input, options) => {
    const { setToNull, preserveEmptyValues } = options || {}
    const cleanObject = obj => {
        if (obj === undefined || obj === null) {
            return undefined
        }
        Object.keys(obj).forEach(key => {
            if (Array.isArray(obj[key])) {
                obj[key] = cleanArray(obj[key])
            }
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                obj[key] = cleanObject(obj[key])
            }
            if (obj[key] === undefined || obj[key] === '' || obj[key] === null) {
                if (setToNull) {
                    obj[key] = null
                } else if (!preserveEmptyValues || obj[key] !== '') {
                    delete obj[key]
                }
            }
        })
        if (setToNull) {
            return Object.keys(obj).find(key => obj[key] !== null) !== undefined
                ? obj
                : null
        }
        return Object.keys(obj).length
            ? obj
            : undefined
    }

    const cleanArray = arr => {
        const clone = arr
            .map(elem => {
                if (Array.isArray(elem)) {
                    return cleanArray(elem)
                }
                if (typeof elem === 'object') {
                    return cleanObject(elem)
                }
                return elem
            })
            .filter(elem => elem !== undefined && elem !== null && elem !== '')
        return clone.length
            ? clone
            : undefined
    }

    if (Array.isArray(input)) {
        return cleanArray(input)
    }
    return cleanObject(input)
}
